<script setup lang="ts">
import type {
  CorporatePageApiModel,
  CorporateStickyBarModel,
  FaqHeaderApiModel,
  FormGridApiModel,
} from '@gro-up/api/corporate'
import type { Form } from '@gro-up/types'
import { useRichTextToHtml } from '@gro-up/ui/composables'
import { computed, defineAsyncComponent, useTemplateRef } from 'vue';

import { useDomain } from '../../composables/use-domain'
import { useComponentMappers } from '../../composables/useComponentMappers'
import type { HeroComponentAlias } from '../../composables/useComponentMappers'
import { useHeroParallax } from '../../composables/useHeroParallax'
import PreFooter from '../blocks/PreFooter/PreFooter.vue'
import StickyBarBlock from '../blocks/StickyBar/StickyBarBlock.vue'
import StickyBarFlupje from '../blocks/StickyBar/StickyBarFlupje.vue'

import HeroArch from './HeroArch.vue'
import PageBlock from './PageBlock.vue'

import { useRuntimeConfig } from '#app'
import { useSeoMeta, useHead } from '#imports'

// Async: the enrollment form (radix-vue, date picker) is only needed on activity pages.
const ActivityEnrollment = defineAsyncComponent(() => import('../blocks/ActivityEnrollment/ActivityEnrollment.vue'))

export type PageProps = {
  page: CorporatePageApiModel
}

const props = defineProps<PageProps>()

const { getBlockComponent } = useComponentMappers()

const domain = useDomain()
const config = useRuntimeConfig()

useHead({
  htmlAttrs: {
    lang: 'nl',
  },
  link: [
    {
      rel: 'canonical',
      href: props.page.canonicalUrl,
    },
  ],
  meta: [
    {
      name: 'robots',
      content:
        config.public.indexable === false
          ? 'noindex, nofollow'
          : `${props.page.noIndex ? 'no' : ''}index, ${props.page.noFollow ? 'no' : ''}follow`,
    },
  ],
  script: props.page.structuredData?.map(data => ({
    type: 'application/ld+json',
    textContent: data,
  })),
})

useSeoMeta({
  title: `${props.page.meta?.title ?? 'gro-up'}`,
  description: props.page.meta?.description,
  ogUrl: props.page.canonicalUrl,
  ogTitle: `${props.page.name ?? 'gro-up'}`,
  ogDescription: props.page.meta?.description,
  ogType: 'website',
  ogImage: props.page.og?.image?.src,
  ogImageAlt: props.page.og?.image?.altText,
  ogImageWidth: props.page.og?.image?.width,
  ogImageHeight: props.page.og?.image?.height,
  ogLocale: 'nl_NL',
  ogSiteName: props.page.siteName ?? 'gro-up',
  twitterCard: 'summary_large_image',
  twitterTitle: `${props.page.twitter?.title ?? 'gro-up'}`,
  twitterDescription: props.page.twitter?.description,
  twitterImage: props.page.twitter?.image?.src,
  twitterImageAlt: props.page.twitter?.image?.altText,
  themeColor: domain.value
    ? {
        'child-care': '#FBF5EA',
        'head-office': '#FAF8F5',
        'community-work': '#FFE5E5',
        'youth-support': '#E9F3F4',
        'maternity-care': '#F8EBD7',
      }[domain.value]
    : '#1F122E',
})

// Handle block errors using a NuxtErrorBoundary here, so that the page will still render even if one block fails to render
// and we won't show a 500 error page unnecessarily to the user
const handleBlockError = (error: unknown, block: CorporatePageApiModel) => {
  console.error(`Failed to render block of type '${block.type}'`, {
    error,
    block,
  })
}

// On an activity page the `corporateForm` block is the sign-up form: it is not
// rendered inline, but surfaced in the enrollment aside (see ActivityEnrollment).
const isActivityPage = computed(() => props.page.type === 'activityPage')
const enrollmentForm = computed<Form | undefined>(() => {
  if (!isActivityPage.value) { return undefined }
  const block = props.page.blocks?.find(block => block.type === 'corporateForm')
  return (block?.value as FormGridApiModel | undefined)?.form as unknown as Form | undefined
})

const HERO_ALIASES: HeroComponentAlias[] = [
  'subHomeHeader',
  'locationHero',
  'locationHeader',
  'neighbourhoodHeader',
  'growArticleHeader',
  'genericHeader',
]
const heros = computed(() => props.page.blocks?.filter(block => HERO_ALIASES.includes(block.type as HeroComponentAlias)) || [])
// The FAQ page sends its header (`faqHeader`) as a separate block, but FaqSection renders the
// header above its search, so it is merged into the `faqOverview` block instead of rendered alone.
const faqHeader = computed(() => props.page.blocks?.find(block => block.type === 'faqHeader')?.value as
  FaqHeaderApiModel | undefined)
const blocks = computed(() => props.page.blocks
  ?.filter(block =>
    !HERO_ALIASES.includes(block.type as HeroComponentAlias)
    && block.type !== 'stickyBar'
    && block.type !== 'faqHeader'
    && !(isActivityPage.value && block.type === 'corporateForm'))
  .map(block => block.type === 'faqOverview' && faqHeader.value
    ? { ...block, value: { ...block.value, header: faqHeader.value.header, body: faqHeader.value.body } }
    : block))
const hasHero = computed(() => props.page.blocks?.some(block => HERO_ALIASES.includes(block.type as HeroComponentAlias)) ?? false)

// Editable per page (CMS), falls back to a site-wide default resolved by the backend. The backend always
// sends a `preFooter` object (even when the editor hasn't enabled it on this page), with an empty
// `articles` array as the "not available" signal — so gate on that rather than mere object presence.
const preFooter = computed(() => {
  const value = props.page.preFooter
  return value && value.articles.length > 0 ? value : undefined
})

// Parallax: the hero scrolls slower than the (opaque) content, so the content
// slides up and over it with the arch as the separation. See useHeroParallax.
const heroContainer = useTemplateRef<HTMLElement>('heroContainer')
useHeroParallax(heroContainer, { enabled: hasHero, speed: 0.4, startMargin: 80 })

const stickyBarData = computed<CorporateStickyBarModel | undefined>(() => {
   const block = props.page.blocks?.find(block => block.type === 'stickyBar')
   if (!block) { return }
   return block.value as CorporateStickyBarModel
})

const { richTextToHtml } = useRichTextToHtml()

const hasStickyBar = computed<boolean>(() => !!stickyBarData.value)

const flupjeTitle = computed<string | undefined>(() => {
  const item = stickyBarData.value?.item
  if (!item) { return undefined }
  if ('title' in item) {
    return item.title
  }
  if ('header' in item) {
    return richTextToHtml(item.header)
  }
  return undefined
})
</script>

<template>
  <div class="flex grow flex-col">
    <div v-if="hasHero" ref="heroContainer" class="hero-container relative z-0">
      <template v-for="(hero, index) in heros" :key="`page-heros-${index}`">
        <NuxtErrorBoundary @error="error => handleBlockError(error, hero)">
          <template v-if="getBlockComponent(hero.type)">
            <component :is="getBlockComponent(hero.type)" v-bind="hero.value" />
          </template>
        </NuxtErrorBoundary>
      </template>
    </div>

    <!--
      De padding moet de curve van de boog vrijhouden. HeroArch schaalt onder `md` mee met de
      viewport (150vw) — de curve is daar maar een paar tientallen pixels diep en valt met de
      -translate-y-12 volledig binnen de hero. Vanaf `md` staat de boog op zijn intrinsieke
      breedte en is de curve de volle 152px diep, dus ~104px onder de bovenkant van deze laag.
      Daarom stapt de padding op dezelfde `md`-breakpoint mee (zie GrowSubhomePage): zonder die
      stap begint de content tussen 768px en 1024px op 48px en loopt hij over de boog heen.
    -->
    <div class="components relative z-10" :class="{ 'pt-12 md:pt-32 lg:pt-48': hasHero }">
      <!--
        Arch: curved separation that the content slides over. Absolutely positioned
        so it doesn't take layout space; pulled slightly up into the hero (-translate-y)
        for the overlap. Centred and horizontally clipped so the fixed-width SVG keeps
        its size on smaller screens with its faded sides overflowing out of view.
        Its opaque body (full width below the curve) covers the hero during parallax,
        and its gradient fades into the page background, so the content needs no bg.
      -->
      <div
        v-if="hasHero"
        class="pointer-events-none absolute inset-x-0 top-0 z-0 flex -translate-y-12 justify-center overflow-x-clip before:absolute before:inset-y-full before:left-0 before:right-0 before:h-full before:bg-head-office-20 before:-translate-y-0.5">
        <HeroArch />
      </div>

      <div class="relative z-10 flex grow flex-col gap-16 lg:gap-32">
        <StickyBarBlock v-if="hasStickyBar && stickyBarData" v-bind="stickyBarData" variant="mobile" />
        <template v-for="(block, index) in blocks" :key="`page-block-${index}`">
          <PageBlock :block="block" />

          <!-- Enrollment button + aside, rendered right under the activity hero. -->
          <div v-if="block.type === 'activityHeader' && enrollmentForm" class="relative">
            <ActivityEnrollment :form="enrollmentForm" :activity-title="page.name" />
          </div>
        </template>
      </div>
    </div>

    <StickyBarBlock v-if="hasStickyBar && stickyBarData" v-bind="stickyBarData" variant="desktop" />
    <StickyBarFlupje v-if="hasStickyBar" :title="flupjeTitle" />

    <!--
      Rendered after the sticky bar (not before): the sticky bar's `position: sticky` range is bounded by
      this whole root div, so anything placed before it extends the range it stays pinned over. Keeping
      PreFooter last means the sticky bar still unpins at the same point it always did, right at the end
      of the regular content, instead of floating over the new footer block underneath it.
    -->
    <div class="relative z-10">
      <PreFooter v-if="preFooter" v-bind="preFooter" />
    </div>
  </div>
</template>


